'use client'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { type FormatData, type FormatPost } from './format-data'
import {
  MediaFrame,
  ProductThumb,
  AddPill,
  Stars,
  usePrefersReducedMotion,
  useAdded,
} from './format-primitives'

// Shop the Look — a lifestyle image with tap-to-shop hotspot pins and a
// "Shop this look" rail. Clicking a pin or a rail row highlights the product;
// pins pulse (reduced-motion gated) to draw the eye. Real per-vertical posts +
// products in, no fabricated media.
export function ShopTheLook({ data }: { data: FormatData }) {
  const reduced = usePrefersReducedMotion()
  const post: FormatPost | undefined = data.posts[0]
  const rail = post ? post.hotspots.map((h) => h.product) : data.products.slice(0, 5)
  const [active, setActive] = React.useState<string | null>(rail[0]?.id ?? null)
  const [added, fireAdded] = useAdded()

  if (!post) return null

  return (
    <div className="flex flex-col overflow-hidden rounded-[var(--r-card)] border bg-card md:flex-row">
      {/* Lifestyle frame with hotspots */}
      <div className="relative min-h-[360px] flex-1 md:min-h-[440px]">
        <MediaFrame
          src={post.image}
          alt={post.caption}
          slug={data.slug}
          seed={1}
          kenBurns={!reduced}
          sizes="(max-width: 768px) 100vw, 55vw"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/20" />
        <div className="absolute left-4 top-4 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] opacity-90">{data.shortName}</p>
          <p className="mt-1 font-display text-lg drop-shadow">{post.user}</p>
        </div>

        {post.hotspots.map((h, i) => {
          const isActive = active === h.product.id
          return (
            <div
              key={h.product.id + i}
              className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${h.x}%`, top: `${h.y}%` }}
            >
              <button
                type="button"
                aria-label={`Shop ${h.product.name}`}
                aria-pressed={isActive}
                onClick={() => setActive(h.product.id)}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-lg ring-2 ring-white/70',
                  'transition-transform duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
                  'focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]',
                  'motion-safe:hover:scale-110',
                  isActive && 'scale-110 ring-[var(--accent)]',
                  !reduced && !isActive && 'motion-safe:animate-[fmt-pulsering_2.4s_ease-out_infinite]',
                )}
              >
                <span className="h-2 w-2 rounded-full bg-[var(--accent)]" />
              </button>

              {isActive && (
                <div className="absolute left-1/2 top-9 z-20 w-44 -translate-x-1/2 rounded-[var(--r-md)] border bg-card p-2.5 shadow-[var(--shadow-lg)] motion-safe:animate-[fmt-slideup_var(--dur)_var(--ease-out)]">
                  <div className="flex items-center gap-2.5">
                    <ProductThumb product={h.product} slug={data.slug} seed={i} size={42} />
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold leading-tight">{h.product.name}</p>
                      <p className="mt-0.5 text-[13px] font-semibold text-primary">{h.product.price}</p>
                    </div>
                  </div>
                  <AddPill
                    className="mt-2 w-full justify-center"
                    onClick={() => fireAdded(h.product.id)}
                    added={added === h.product.id}
                    label="Add to bag"
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Shop this look rail */}
      <aside className="flex w-full flex-col bg-card p-4 md:max-w-[360px] md:p-5">
        <div className="mb-1 flex items-baseline justify-between border-b pb-3">
          <h3 className="font-display text-lg">Shop this look</h3>
          <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
            {rail.length} items
          </span>
        </div>
        <ul className="divide-y">
          {rail.map((p, i) => (
            <li key={p.id + i}>
              <div
                role="button"
                tabIndex={0}
                onClick={() => setActive(p.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setActive(p.id)
                  }
                }}
                aria-pressed={active === p.id}
                className={cn(
                  'flex w-full items-center gap-3 py-3 text-left transition-colors duration-[var(--dur-fast)] cursor-pointer',
                  'focus-visible:outline-none focus-visible:[box-shadow:var(--ring)]',
                  active === p.id && 'bg-[color-mix(in_srgb,var(--accent)_6%,transparent)]',
                )}
              >
                <ProductThumb product={p} slug={data.slug} seed={i} size={54} />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">{p.tag}</p>
                  <p className="mt-0.5 truncate text-sm font-semibold">{p.name}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm font-semibold">{p.price}</span>
                    <Stars rating={p.rating} />
                  </div>
                </div>
                <AddPill onClick={() => fireAdded(p.id)} added={added === p.id} label="Add" />
              </div>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  )
}
